/**
 * Pre-redesign disbursements table, moved here unchanged so the AD_DISBURSEMENTS_LIST_V2 flag has a
 * byte-identical off path. The Material-UI imports below are the original ones, not new usage — this
 * file is deleted once the flag is removed, so it is deliberately not migrated to Lemonade.
 */
import { TableCell, TableRow, Box } from '@material-ui/core'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BulkMarkAsPaidModal } from '../bulkMarkAsPaidModal/BulkMarkAsPaidModal'
import { CreateDisbursementModal } from '../createDisbursementModal/CreateDisbursementModal'
import { getDisbursementBatchType } from '../disbursementDetails/DisbursementFoundationDetails/DisbursementFoundationDetails'

import { Badge, Icon, Button } from '@percent/lemonade'
import { ErrorView, FoundationNameWithFlag, Table } from '@percent/admin-dashboard/common/components'
import { useFoundations, usePermissions } from '@percent/admin-dashboard/common/hooks'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { DisbursementsTableProps } from './DisbursementsTable.types'
import styles from './DisbursementsTableLegacy.module.scss'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { DisbursementsFiltersLegacy } from './disbursementsFilters/DisbursementsFiltersLegacy'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'

export function DisbursementsTableLegacy({
  isLoading,
  totalResults,
  data,
  previousPage,
  nextPage,
  refreshToFirstPage,
  errorMessage,
  queryParams,
  setQueryParams
}: DisbursementsTableProps) {
  const { t } = useTranslation()
  const { foundations } = useFoundations()
  const navigate = useNavigate()
  const { userHasPermissions } = usePermissions()
  const [isCreateDisbursementModalOpen, setIsCreateDisbursementModalOpen] = useState(false)
  const [isBulkMarkAsPaidModalOpen, setIsBulkMarkAsPaidModalOpen] = useState(false)

  const logReturnedPaymentFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_LOG_RETURNED_PAYMENT)
  const internalFgaDoraDisbursementsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_DISBURSEMENTS
  )
  const multiApprovalFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_MULTI_APPROVAL)

  const canCreateBatch = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_create')
  const canMarkAsPaid = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_mark_paid')

  const cells = [
    { id: 'name', isSortable: false, props: { width: '200px' } },
    { id: 'foundation', isSortable: false, props: { width: '152px' } },
    { id: 'disbursements', isSortable: false, props: { width: '140px' } },
    { id: 'pendingApproval', isSortable: false, props: { width: '150px' } },
    { id: 'pendingPayment', isSortable: false, props: { width: '160px' } },
    { id: 'sent', isSortable: false, props: { width: '90px' } },
    { id: 'paid', isSortable: false, props: { width: '90px' } },
    ...(logReturnedPaymentFeatureFlag ? [{ id: 'returned', isSortable: false, props: { width: '90px' } }] : []),
    ...(multiApprovalFeatureFlag ? [{ id: 'failed', isSortable: false, props: { width: '90px' } }] : []),
    { id: 'removed', isSortable: false, props: { width: '110px' } },
    { id: 'donationsUpTo', isSortable: false, props: { width: '200px' } },
    { id: 'type', isSortable: false, props: { width: '100px' } }
  ]

  const tableHeader = (
    <div className={styles.tableHeader}>
      <Box className={styles.tableHeaderButton}>
        <DisbursementsFiltersLegacy queryParams={queryParams} setQueryParams={setQueryParams} />
        {canMarkAsPaid && (
          <Button onClick={() => setIsBulkMarkAsPaidModalOpen(true)} size="small">
            {t('button.bulkMarkAsPaid')}
          </Button>
        )}
        <Button onClick={() => setIsCreateDisbursementModalOpen(true)} size="small" disabled={!canCreateBatch}>
          {t('button.createDisbursementBatch')}
        </Button>
      </Box>
    </div>
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <Table
        isLoading={isLoading}
        totalResults={totalResults}
        title={tableHeader}
        data={data}
        previousPage={previousPage}
        nextPage={nextPage}
        columns={cells}
        emptyTableText={t('table.emptyDisbursements')}
      >
        {data?.map(row => (
          <TableRow
            className={styles.tableRow}
            key={row.id}
            onClick={handleClickAsLink(`/disbursements/${row.id}`, navigate)}
          >
            <TableCell title={row.name} className={styles.nameCell}>
              {row.name}
            </TableCell>
            <TableCell>
              {foundations?.[row.foundationId] ? (
                <FoundationNameWithFlag
                  countryCode={foundations[row.foundationId].countryCode}
                  name={foundations[row.foundationId].name}
                />
              ) : (
                row.foundationId
              )}
            </TableCell>
            <TableCell>
              <Badge variant="default" icon="claims">
                {row.counts.total}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant="informative" icon="clock">
                {row.counts.approvalRequired +
                  row.counts.trusteeApprovalRequired +
                  row.counts.agentApprovalRequired +
                  row.counts.managerApprovalRequired}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant="default" icon="check-badge">
                {row.counts.approved}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant="informative" icon="check-badge">
                {row.counts.inTransit + (row.counts.paymentInitiated ?? 0)}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant="positive" icon="approve">
                {row.counts.paid}
              </Badge>
            </TableCell>
            {logReturnedPaymentFeatureFlag && (
              <TableCell>
                <Badge variant="critical" icon="warning">
                  {row.counts.returned ?? 0}
                </Badge>
              </TableCell>
            )}
            {multiApprovalFeatureFlag && (
              <TableCell>
                <Badge variant="critical" icon="reject">
                  {row.counts.failed ?? 0}
                </Badge>
              </TableCell>
            )}
            <TableCell>
              <Badge variant="critical" icon="reject">
                {row.counts.cancelled}
              </Badge>
            </TableCell>
            <TableCell>{dayJS(row.endDate).format('DD MMM YYYY')}</TableCell>
            <TableCell>{getDisbursementBatchType(row.detailedCounts)}</TableCell>
            <TableCell align="right" style={{ padding: '16px 0' }}>
              <Icon name="chevron-right" size="s" />
            </TableCell>
          </TableRow>
        ))}
      </Table>
      <CreateDisbursementModal
        openModal={isCreateDisbursementModalOpen}
        onClose={() => setIsCreateDisbursementModalOpen(false)}
        refreshDisbursementsTable={refreshToFirstPage}
      />
      {isBulkMarkAsPaidModalOpen && (
        <BulkMarkAsPaidModal onClose={() => setIsBulkMarkAsPaidModalOpen(false)} refresh={refreshToFirstPage} />
      )}
    </>
  )
}
